// bench-STAFF-PASSWORD-RESET-SCREEN.CB-B §1.7. Both calls go through secureFetch directly —
// NEVER through enqueue()/IndexedDB. A password must not rest in the offline queue; this is
// the one named exception to offline-first (the screen disables the action instead, §1.8).
import { secureFetch } from './auth'
import { failureMessage } from './orders-api'

export interface StaffRow {
  id: string
  name: string
  username: string
  roles: string[]
  active: boolean
  canReset: boolean
  canManage: boolean
}

export class StaffApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

export async function fetchStaff(): Promise<StaffRow[]> {
  const res = await secureFetch('/api/auth/staff')
  if (!res.ok) throw new StaffApiError(await failureMessage(res), res.status)
  return res.json() as Promise<StaffRow[]>
}

export async function resetStaffPassword(id: string, newPassword: string): Promise<void> {
  const res = await secureFetch(`/api/auth/staff/${id}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newPassword }),
  })
  if (!res.ok) throw new StaffApiError(await failureMessage(res), res.status)
}

export async function deactivateStaff(id: string): Promise<void> {
  const res = await secureFetch(`/api/auth/staff/${id}/deactivate`, { method: 'POST' })
  if (!res.ok) throw new StaffApiError(await failureMessage(res), res.status)
}

export async function reactivateStaff(id: string): Promise<void> {
  const res = await secureFetch(`/api/auth/staff/${id}/reactivate`, { method: 'POST' })
  if (!res.ok) throw new StaffApiError(await failureMessage(res), res.status)
}

export async function signOutEverywhere(id: string): Promise<void> {
  const res = await secureFetch(`/api/auth/staff/${id}/sign-out-everywhere`, { method: 'POST' })
  if (!res.ok) throw new StaffApiError(await failureMessage(res), res.status)
}

export async function changeOwnPassword(currentPassword: string, newPassword: string): Promise<void> {
  const res = await secureFetch('/api/auth/me/password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ currentPassword, newPassword }),
  })
  if (!res.ok) throw new StaffApiError(await failureMessage(res), res.status)
}

// R413 (c): the roles an owner/architect may grant, for the create form and the edit-roles
// picker. The server answers [{ id, slug }]; the screen labels each by its slug.
export interface GrantableRole {
  id: string
  slug: string
}

export async function fetchGrantableRoles(): Promise<GrantableRole[]> {
  const res = await secureFetch('/api/auth/roles')
  if (!res.ok) throw new StaffApiError(await failureMessage(res), res.status)
  return res.json() as Promise<GrantableRole[]>
}

export interface CreateStaffInput {
  name: string
  username: string
  initialPassword: string
  roleIds: string[]
}

export async function createStaff(input: CreateStaffInput): Promise<{ id: string; username: string }> {
  const res = await secureFetch('/api/auth/staff', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: input.name,
      username: input.username,
      initialPassword: input.initialPassword,
      roleIds: input.roleIds,
    }),
  })
  if (!res.ok) throw new StaffApiError(await failureMessage(res), res.status)
  return res.json() as Promise<{ id: string; username: string }>
}

// The server signs this person out everywhere when their roles change.
export async function setStaffRoles(id: string, roleIds: string[]): Promise<void> {
  const res = await secureFetch(`/api/auth/staff/${id}/roles`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roleIds }),
  })
  if (!res.ok) throw new StaffApiError(await failureMessage(res), res.status)
}
